import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ErrorPanelService } from '../../services/error-panel.service';
import { CalendarEvent } from 'angular-calendar';
import { EventColor } from 'calendar-utils';
import { CalendarAdapterService } from './calendar-adapter.service';
import {
  modifyCalendarEvent,
  newCalendarEvent,
  newPatientAndAppointment,
} from '../../models/calendar-model';
import { CalendarUtilsService } from './calendar-utils.service';

declare const gapi: any;

@Injectable({
  providedIn: 'root',
})
export class GoogleCalendarService {
  constructor(
    private zone: NgZone,
    private errorPanelService: ErrorPanelService,
    private calendarUtilsService: CalendarUtilsService
  ) {}

  // This now returns raw Google Calendar events
  loadEventsStartingFromPreviousMonth(
    token: string,
    calendarId: string
  ): Promise<any[]> {
    const now = new Date();
    const startOfPreviousMonth = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1
    );

    return new Promise<any[]>((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          gapi.auth.setToken({ access_token: token });
          gapi.client.calendar.events
            .list({
              calendarId: calendarId,
              timeMin: startOfPreviousMonth.toISOString(),
              showDeleted: false,
              singleEvents: true,
              maxResults: 2500,
              orderBy: 'startTime',
            })
            .then((response: { result: { items: any[] } }) => {
              resolve(response.result.items);
            })
            .catch((error: any) => {
              console.error(
                `Error loading events from calendar ${calendarId}:`,
                error
              );
              this.errorPanelService.showErrorSnackBar(
                `Errore nel caricare gli eventi dal calendario ${calendarId}. Prova a rifare il login`
              );
              reject(error);
            });
        });
      });
    });
  }

  loadCalendars(token: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          gapi.auth.setToken({ access_token: token });
          gapi.client.calendar.calendarList
            .list()
            .then((response: { result: { items: any[] } }) => {
              const calendars = response.result.items;
              resolve(calendars);
            })
            .catch((error: any) => {
              console.error('Error loading calendars:', error);
              this.errorPanelService.showErrorSnackBar(
                'Errore nel caricare i calendari. Prova a rifare il login'
              );
              reject(error);
            });
        });
      });
    });
  }

  addEventToGoogleCalendar(newEvent: newCalendarEvent): Promise<any> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          const token = sessionStorage.getItem('googleAccessToken');
          gapi.auth.setToken({ access_token: token });

          const eventDate = this.calendarUtilsService.formatDate(newEvent.date);
          const eventDateTime =
            this.calendarUtilsService.combineDateTimeAndLength(
              newEvent.date,
              newEvent.time,
              newEvent.lenght.toString()
            );

          let event = {};
          if (newEvent.isAllDay) {
            event = {
              summary: newEvent.title,
              location: newEvent.calendarName,
              start: {
                date: eventDate,
              },
              end: {
                date: eventDate,
              },
            };
          } else {
            event = {
              summary: newEvent.title,
              location: newEvent.calendarName,
              start: {
                dateTime: eventDateTime.start.toISOString(),
                timeZone: 'Europe/Rome',
              },
              end: {
                dateTime: eventDateTime.end.toISOString(),
                timeZone: 'Europe/Rome',
              },
            };
          }

          gapi.client.calendar.events
            .insert({
              calendarId: newEvent.calendarId,
              resource: event,
            })
            .then((response: any) => {
              resolve(response);
            })
            .catch((error: any) => {
              console.error(
                `Error adding event to calendar ${newEvent.calendarId}:`,
                error
              );
              this.errorPanelService.showErrorSnackBar(
                "Errore nel creare l'evento. Prova a rifare il login"
              );
              reject(error);
            });
        });
      });
    });
  }

  modifyCalendarEvent(
    modifyEvent: modifyCalendarEvent,
    calendarId: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          const token = sessionStorage.getItem('googleAccessToken');
          gapi.auth.setToken({ access_token: token });

          const startTimeISO = this.calendarUtilsService.convertDateTime(
            modifyEvent.date,
            modifyEvent.start_time
          );
          const endTimeISO = this.calendarUtilsService.convertDateTime(
            modifyEvent.date,
            modifyEvent.end_time
          );

          let event = {};
          if (modifyEvent.isAllDay) {
            event = {
              summary: modifyEvent.title,
              start: {
                date: this.calendarUtilsService.formatDate(modifyEvent.date),
              },
              end: {
                date: this.calendarUtilsService.formatDate(modifyEvent.date),
              },
            };
          } else {
            event = {
              summary: modifyEvent.title,
              start: {
                dateTime: startTimeISO,
                timeZone: 'Europe/Rome',
              },
              end: {
                dateTime: endTimeISO,
                timeZone: 'Europe/Rome',
              },
            };
          }

          gapi.client.calendar.events
            .update({
              calendarId: calendarId,
              eventId: modifyEvent.id,
              resource: event,
            })
            .then((response: any) => {
              resolve(response);
            })
            .catch((error: any) => {
              console.error(`Error updating event in calendar:`, error);
              this.errorPanelService.showErrorSnackBar(
                "Errore nell'aggiornare l'evento. Prova a rifare il login"
              );
              reject(error);
            });
        });
      });
    });
  }

  deleteCalendarEvent(eventId: string, calendarId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          const token = sessionStorage.getItem('googleAccessToken');
          gapi.auth.setToken({ access_token: token });

          gapi.client.calendar.events
            .delete({
              calendarId: calendarId,
              eventId: eventId,
            })
            .then(() => {
              resolve(true);
            })
            .catch((error: any) => {
              console.error('Error deleting event:', error);
              this.errorPanelService.showErrorSnackBar(
                "Errore nella eliminazione dell'evento. Prova a rifare il login"
              );
              reject(error);
            });
        });
      });
    });
  }

  loadPsicogeniusCalendar(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          gapi.auth.setToken({ access_token: token });
          gapi.client.calendar.calendarList
            .list()
            .then((response: { result: { items: any[] } }) => {
              const calendars = response.result.items;

              const index = calendars.findIndex(
                (calendar) => calendar.summary === 'Psicogenius'
              );
              var psicogeniusCalendar: any;
              if (index !== -1) {
                psicogeniusCalendar = calendars[index];
                sessionStorage.setItem(
                  'PsicogeniusCalendarId',
                  psicogeniusCalendar.id
                );
                resolve();
              } else {
                // Calendar doesn't exist, create it
                this.createPsicogeniusCalendar()
                  .then((newCalendar) => {
                    console.log('Psicogenius calendar created:', newCalendar);
                    resolve();
                  })
                  .catch((error) => {
                    console.error(
                      'Error creating Psicogenius calendar:',
                      error
                    );
                    reject(error);
                  });
              }
            })
            .catch((error: any) => {
              console.error('Error loading calendars:', error);
              this.errorPanelService.showErrorSnackBar(
                'Errore nel caricare i calendari. Prova a rifare il login'
              );
              reject(error);
            });
        });
      });
    });
  }

  private createPsicogeniusCalendar(): Promise<any> {
    return new Promise((resolve, reject) => {
      const requestBody = {
        summary: 'Psicogenius',
        timeZone: 'Europe/Rome',
      };

      gapi.client.calendar.calendars
        .insert({
          resource: requestBody,
        })
        .then(
          (response: { result: unknown }) => {
            const calendar = response.result;
            sessionStorage.setItem(
              'PsicogeniusCalendarId',
              (calendar as any).id
            );
            resolve(calendar);
          },
          (err: any) => reject(err)
        );
    });
  }

  addPatientAppointment(newEvent: newPatientAndAppointment): Promise<any> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          const token = sessionStorage.getItem('googleAccessToken');
          gapi.auth.setToken({ access_token: token });

          const eventDateTime =
            this.calendarUtilsService.combineDateTimeAndLength(
              newEvent.date,
              newEvent.time,
              newEvent.lenght
            );

          const event = {
            summary: newEvent.title,
            location: 'Psicogenius',
            start: {
              dateTime: eventDateTime.start.toISOString(),
              timeZone: 'Europe/Rome',
            },
            end: {
              dateTime: eventDateTime.end.toISOString(),
              timeZone: 'Europe/Rome',
            },
          };

          gapi.client.calendar.events
            .insert({
              calendarId: sessionStorage.getItem('PsicogeniusCalendarId'),
              resource: event,
            })
            .then((response: any) => {
              resolve(response);
            })
            .catch((error: any) => {
              console.error(`Error adding event to calendar`, error);
              this.errorPanelService.showErrorSnackBar(
                'Errore nel creare un appuntamento nel calendario. Prova a rifare il login'
              );
              reject(error);
            });
        });
      });
    });
  }

  modifyPatientInterviewAppointment(
    newEvent: newPatientAndAppointment,
    eventId: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          const token = sessionStorage.getItem('googleAccessToken');
          gapi.auth.setToken({ access_token: token });

          const eventDateTime =
            this.calendarUtilsService.combineDateTimeAndLength(
              newEvent.date,
              newEvent.time,
              newEvent.lenght
            );

          const event = {
            summary: newEvent.title,
            start: {
              dateTime: eventDateTime.start.toISOString(),
              timeZone: 'Europe/Rome',
            },
            end: {
              dateTime: eventDateTime.end.toISOString(),
              timeZone: 'Europe/Rome',
            },
          };

          gapi.client.calendar.events
            .update({
              calendarId: sessionStorage.getItem('PsicogeniusCalendarId'),
              eventId: eventId,
              resource: event,
            })
            .then((response: any) => {
              resolve(response);
            })
            .catch((error: any) => {
              console.error(`Error updating event in calendar:`, error);
              this.errorPanelService.showErrorSnackBar(
                "Errore nel modificare l' appuntamento nel calendario. Prova a rifare il login"
              );
              reject(error);
            });
        });
      });
    });
  }

  addPatientInterviewAppointment(
    newEvent: newPatientAndAppointment
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          const token = sessionStorage.getItem('googleAccessToken');
          gapi.auth.setToken({ access_token: token });

          const eventDateTime =
            this.calendarUtilsService.combineDateTimeAndLength(
              newEvent.date,
              newEvent.time,
              newEvent.lenght
            );

          const event = {
            summary: newEvent.title,
            location: 'Psicogenius',
            start: {
              dateTime: eventDateTime.start.toISOString(),
              timeZone: 'Europe/Rome',
            },
            end: {
              dateTime: eventDateTime.end.toISOString(),
              timeZone: 'Europe/Rome',
            },
          };

          gapi.client.calendar.events
            .insert({
              calendarId: sessionStorage.getItem('PsicogeniusCalendarId'),
              resource: event,
            })
            .then((response: any) => {
              resolve(response);
            })
            .catch((error: any) => {
              console.error(`Error adding event to calendar`, error);
              this.errorPanelService.showErrorSnackBar(
                "Errore nell'aggiungere l'evento nel calendario. Prova a rifare il login"
              );
              reject(error);
            });
        });
      });
    });
  }

  deletePatientInterviewAppointment(eventId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      gapi.load('client', () => {
        gapi.client.setApiKey(environment.googleAPIKey);
        gapi.client.load('calendar', 'v3', () => {
          const token = sessionStorage.getItem('googleAccessToken');
          gapi.auth.setToken({ access_token: token });

          gapi.client.calendar.events
            .delete({
              calendarId: sessionStorage.getItem('PsicogeniusCalendarId'),
              eventId: eventId,
            })
            .then(() => {
              resolve();
            })
            .catch((error: any) => {
              console.error('Error deleting event:', error);
              this.errorPanelService.showErrorSnackBar(
                "Errore nel cancellare l'evento nel calendario. Prova a rifare il login"
              );
              reject(error);
            });
        });
      });
    });
  }
}
